import { useEffect, useRef } from 'react';
import classnames from 'classnames';

import { getErrorMessage } from '@common/utils/errorMessage';
import { handleError } from '@common/utils/handleError';
import { DatoEmbedRecord } from '@interfaces';

import styles from './soundcloud-embed.module.scss';

type SoundCloudEmbedData = {
	author_name: string;
	author_url: string;
	description: string;
	height: number;
	html: string;
	provider_name: string;
	provider_url: string;
	thumbnail_url: string;
	title: string;
	type: string;
	version: number;
	width: string;
};

type SoundCloudEmbedProps = {
	className?: string;
	embed: DatoEmbedRecord;
};

export const SoundCloudEmbed = ({
	className,
	embed: { url },
}: SoundCloudEmbedProps) => {
	const embedRef = useRef<HTMLDivElement>(null);
	const isCompact = !url.includes('/sets/');

	useEffect(() => {
		if (!embedRef.current) {
			return;
		}

		fetch(`https://soundcloud.com/oembed?url=${url}`)
			.then((response) => response.json())
			.then((data: SoundCloudEmbedData) => {
				// Convert the HTML into a document fragment so we can make some changes to it
				const documentFragment = document
					.createRange()
					.createContextualFragment(data.html);

				const iframeElement = documentFragment.querySelector('iframe');

				if (iframeElement) {
					// Update iframe src and attributes
					const srcUrl = new URL(iframeElement.src);

					srcUrl.searchParams.set('show_comments', 'false');
					srcUrl.searchParams.set('visual', 'false');

					iframeElement.setAttribute(
						'title',
						`${data.provider_name} Embed: ${data.title}`,
					);
					iframeElement.setAttribute('height', '100%');
					iframeElement.setAttribute('loading', 'lazy');
					iframeElement.setAttribute('src', srcUrl.toString());
				}

				if (embedRef.current) {
					embedRef.current.replaceChildren(documentFragment);
				}
			})
			.catch((error) => {
				handleError(getErrorMessage(error));
			});
	}, [url, embedRef]);

	return (
		<div
			className={classnames(className, styles.embed, {
				[styles['is-compact']]: isCompact,
			})}
			ref={embedRef}
		/>
	);
};
